import type { RouterOutputs } from "@apps/server/trpc/routers";
import { Accordion, Badge, Box, Center, Group, Skeleton, Stack, type StackProps, Text, Title } from "@mantine/core";
import { Dropzone, PDF_MIME_TYPE } from "@mantine/dropzone";
import { IconPdf, IconUpload, IconX } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, type ReactNode, useEffect, useMemo, useState } from "react";
import { type FileUploadEntry, useFileUpload } from "~/hooks/use-file-upload";
import { logger } from "~/lib/logger";
import { trpc } from "~/lib/trpc";

export const Route = createFileRoute("/_authenticated/accounting/upload-incoming-skv-documents")({
  component: RouteComponent,
});

type ParsedData = RouterOutputs["pdfParser"]["parsePdf"]["parsedData"];

function RouteComponent() {
  const { add, remove, uploads, reset } = useFileUpload({ folder: "docs-from-skv", type: "private" });

  return (
    <Box p="md">
      <Title order={3} mb="lg">
        Upload pdf documents received from Skatteverket
      </Title>
      <Dropzone
        radius="md"
        onDrop={(files) => {
          logger.debug("accepted files", files);
          add(files);
        }}
        onReject={(files) => logger.debug("rejected files", files)}
        maxSize={50 * 1024 ** 2}
        accept={PDF_MIME_TYPE}
        // multiple={false}
      >
        <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: "none" }}>
          <Dropzone.Accept>
            <IconUpload size={52} color="var(--mantine-color-blue-6)" />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={52} color="var(--mantine-color-red-6)" />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPdf size={52} color="var(--mantine-color-red-4)" />
          </Dropzone.Idle>

          <div>
            <Text size="xl" inline>
              Drag PDF documents or a folder with PDFs
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              Select one or more files. The file size should not exceed 50mb.
            </Text>
          </div>
        </Group>
      </Dropzone>
      {uploads.length > 0 && (
        <Accordion order={3} defaultValue="Apples" my="lg">
          {uploads.map((upload) => (
            <UploadedFileDisplay
              key={upload.file.name + upload.file.size}
              upload={upload}
              remove={() => remove(upload.fileId || "")}
            />
          ))}
        </Accordion>
      )}
    </Box>
  );
}

function UploadedFileDisplay({ upload }: { upload: FileUploadEntry; remove: () => void }) {
  const [parsedData, setParsedData] = useState<ParsedData>();
  const [isExisting, setExisting] = useState<boolean>();

  const parsePdf = useMutation(
    trpc.pdfParser.parsePdf.mutationOptions({
      onSuccess: (data) => {
        setParsedData(data.parsedData);
        setExisting(data.isExisting);
      },
    }),
  );

  useEffect(() => {
    if (parsedData) return;
    if (!upload) return;
    if (upload.status !== "done") return;
    if (!upload.fileId) return;
    parsePdf.mutate({ fileId: upload.fileId });
  }, [upload, parsedData]);

  const titleData = useMemo(() => {
    if (!parsedData) return null;
    return {
      representative: parsedData.data.representative.name,
      title: parsedData.title,
      reference_number: parsedData.data.reference_number,
      receivedDate: parsedData.data.received_date,
      type: parsedData.document_type,
    };
  }, [parsedData]);

  // if (parsePdf.isPending) {
  //   return <Alert color="blue">Parsing document...</Alert>
  // }

  // if (parsePdf.isError) {
  //   return <Alert color="red">{parsePdf.error.message}</Alert>
  // }

  return (
    <Accordion.Item value={upload.file.name + upload.file.size}>
      <Accordion.Control icon={<IconPdf size={24} color="var(--mantine-color-red-5)" />}>
        <Group gap={40} align="center" justify="space-between" wrap="nowrap">
          <Group>
            <TitleInfo title={upload.file.name} miw={250}>
              {titleData?.title}
            </TitleInfo>
            <Activity mode={isExisting === undefined ? "hidden" : "visible"}>
              <Center h="100%">
                {isExisting ? (
                  <Badge color="orange.2" variant="light" size="lg">
                    <Text c="orange" fz={10} fw="bold">
                      Duplicate
                    </Text>
                  </Badge>
                ) : (
                  <Badge color="cyan.4" variant="light" size="lg">
                    <Text c="lime" fz={10} fw="bold">
                      Uploaded
                    </Text>
                  </Badge>
                )}
              </Center>
            </Activity>
          </Group>
          <Group gap={40}>
            <TitleInfo title="Representative" miw={180}>
              {titleData?.representative}
            </TitleInfo>
            <TitleInfo title="Type" miw={100}>
              {titleData ? <DocumentType type={titleData.type} /> : undefined}
            </TitleInfo>
            <TitleInfo title="Reference Number" miw={125}>
              {titleData?.reference_number}
            </TitleInfo>
            <TitleInfo title="Received on" miw={100}>
              {titleData?.receivedDate}
            </TitleInfo>
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Box mt="md">
          <pre>{JSON.stringify(parsedData, null, 2)}</pre>
        </Box>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

type TitleInfoProps = {
  title: string;
  children: ReactNode | undefined;
} & StackProps;

function TitleInfo({ title, children, ...props }: TitleInfoProps) {
  return (
    <Stack gap={6} {...props}>
      <Text size="xs" c="dimmed" tt="uppercase" lh={1} fw={600} style={{ letterSpacing: "0.04em" }}>
        {title}
      </Text>
      {children === undefined ? (
        <Skeleton h={20} animate />
      ) : typeof children === "string" ? (
        <Text size="sm" fw={500} lh={1.3}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Stack>
  );
}

function DocumentType({ type }: { type: ParsedData["document_type"] }) {
  if (type === "rut-decision") {
    return (
      <Badge color="lime" variant="light">
        Decision
      </Badge>
    );
  }
  if (type === "rut-suggestion") {
    return (
      <Badge color="violet" variant="light">
        Suggestion
      </Badge>
    );
  }

  if (type === "rut-questions") {
    return (
      <Badge color="orange" variant="light">
        Question
      </Badge>
    );
  }
}
